
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Worksheet {
  id: number;
  name: string;
  subject: string;
  grade_level: string;
  semester: string;
  fs_name: string;
  document_urls: string[] | null;
  status: string | null;
}

export const useWorksheetSearch = (initialQuery: string = '') => {
  const [searchQuery, setSearchQuery] = useState(initialQuery || '');

  const fetchWorksheets = async (searchTerm: string): Promise<Worksheet[]> => {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('worksheets')
        .select('id, name, subject, grade_level, semester, fs_name, document_urls, status')
        .ilike('search_index', `%${searchTerm}%`)
        .order('name', { ascending: true })
        .limit(10);

      if (error) {
        console.error('Error fetching worksheets:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('Exception fetching worksheets:', err);
      return [];
    }
  };

  const {
    data = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['worksheets', searchQuery],
    queryFn: () => fetchWorksheets(searchQuery),
    enabled: searchQuery.length > 1,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false, // Don't retry on failure to prevent error loops
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  return {
    worksheets: data || [],
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
  };
};
